import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const RevealLine = ({ children, delay = 0, className = "" }) => (
  <motion.span
    className={`block overflow-hidden pb-[0.14em] -mb-[0.14em] ${className}`}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-40px" }}
  >
    <motion.span
      className="block"
      variants={{
        hidden: { y: "115%" },
        visible: { y: 0, transition: { duration: 1, delay, ease: EASE } },
      }}
    >
      {children}
    </motion.span>
  </motion.span>
);

export const RichLine = ({ segments }) => (
  <>
    {segments.map((s, i) =>
      s.i ? (
        <em key={i} className="italic font-normal text-gradient-fire">{s.t}</em>
      ) : (
        <span key={i}>{s.t}</span>
      )
    )}
  </>
);

export const EditorialHeading = ({ lines, kicker, className = "", size = "text-4xl sm:text-5xl lg:text-6xl" }) => (
  <div className={className}>
    {kicker && (
      <RevealLine delay={0}>
        <span className="font-tech text-[11px] sm:text-xs tracking-[0.4em] uppercase text-neutral-400">— {kicker}</span>
      </RevealLine>
    )}
    <h2 className={`font-display ${size} font-medium leading-[1.08] tracking-tight text-neutral-900 mt-4`}>
      {lines.map((line, i) => (
        <RevealLine key={i} delay={0.12 + i * 0.14}>
          <RichLine segments={line} />
        </RevealLine>
      ))}
    </h2>
  </div>
);

const ScrubWord = ({ progress, range, word }) => {
  const opacity = useTransform(progress, range, [0.12, 1]);
  return (
    <span className="inline-block mr-[0.28em]">
      <motion.span style={{ opacity }} className="inline-block">{word}</motion.span>
    </span>
  );
};

export const ScrubWords = ({ text, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.5"] });
  const words = text.split(" ");
  return (
    <p ref={ref} className={className}>
      {words.map((w, i) => (
        <ScrubWord key={i} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} word={w} />
      ))}
    </p>
  );
};

export const ImageReveal = ({ src, alt = "", className = "" }) => (
  <motion.div
    className={`overflow-hidden ${className}`}
    initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
    whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1.1, ease: [0.77, 0, 0.175, 1] }}
  >
    <motion.img
      src={src}
      alt={alt}
      initial={{ scale: 1.3 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
      className="w-full h-full object-cover"
    />
  </motion.div>
);
